import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductListActions from "@tJohnstonCompanies/Overrides/Widgets/ProductList/ProductListActions";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct;

export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItemCustom?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    DiscontinuedGridItem?: GridItemProps;
    contactUs?: InjectableCss;
    discontinuedMsg?: InjectableCss;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 25px 50px;
                    `,
                    css`
                        padding: 25px 50px;
                    `,
                    css`
                        padding: 25px 50px;
                    `,
                    css`
                        padding: 10px 0;
                    `,
                    css`
                        padding: 10px 0;
                    `,
                ])}
            min-width: 320px; /* prevent overlap with second column */
            > div[class^="GridOffset"] {
                background: #fff;
                border-radius: 7px;
                // border: 1px solid rgb(239, 239, 239);
                // box-shadow: rgba(0, 0, 0, 0.1) 0px 3px 5px 0px;
                border: 1px solid rgb(204 204 204);
                box-shadow: rgba(0, 0, 0, 0.1) 0px 3px 5px 0px;
            }
        `,
    },
    leftColumnGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            padding: 20px;
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [12, 12, 12, 9, 9],
        css: css`
            padding: 20px;
        `,
    },
    infoGridItem: {
        width: [12, 12, 12, 8, 8],
        css: css`
            > a[class^="StyledButton"] > span[class^="TypographyStyle"] {
                background: red;
            }
        `,
    },

    actionsGridItemCustom: {
        width: [12, 12, 12, 4, 4],
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
    DiscontinuedGridItem: {
        width: 12,
        css: css`
            display: block;
            flex-wrap: wrap;
            color: rgb(162, 16, 16);
            font-weight: 600;
        `,
    },
    contactUs: {
        css: css`
            > span[class^="TypographyStyle"] {
                text-decoration: underline;
            }
        `,
    },
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = props => {
    const [discontinued, setDiscontinued] = React.useState(false);

    React.useEffect(() => {
        if (props.product?.properties["productDiscontinued"]?.toLowerCase() === "true") {
            setDiscontinued(true);
        }
    }, []);
    return (
        <GridContainer {...styles.gridContainer} data-test-selector={`productListProductCard${props.product.id}`}>
            <GridItem {...styles.leftColumnGridItem}>
                {props.product.isSponsored && (
                    <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                )}
                <ProductListProductImage
                    showImage={props.showImage}
                    showCompare={props.showCompare}
                    showImageBadge={props.showImageBadges}
                    showTextBadge={props.showTextBadges}
                />
            </GridItem>
            <GridItem {...styles.rightColumnGridItem}>
                <GridContainer {...styles.rightColumnGridContainer}>
                    <GridItem {...styles.infoGridItem}>
                        <ProductListProductInformation
                            showBrand={props.showBrand}
                            showTitle={props.showTitle}
                            showPartNumbers={props.showPartNumbers}
                            showAvailability={props.showAvailability}
                            showAttributes={props.showAttributes}
                            showImageBadges={props.showImageBadges}
                            maxImageBadges={props.maximumNumberOfImageBadges}
                            showTextBadges={props.showTextBadges}
                            maxTextBadges={props.maximumNumberOfTextBadges}
                        />
                    </GridItem>
                    <GridItem {...styles.actionsGridItemCustom}>
                        <ProductListActions
                            showPrice={props.showPrice}
                            showAddToList={props.showAddToList}
                            view="list"
                        />
                    </GridItem>
                    {discontinued && (
                        <GridItem {...styles.DiscontinuedGridItem} data-test-selector="discontinued_product">
                            <Typography {...styles.discontinuedMsg}>
                                {translate(" This item is now discontinued. Please")}
                            </Typography>
                            &nbsp;
                            <Link {...styles.contactUs} href="/ContactUs">
                                {translate("Contact us")}
                            </Link>{" "}
                            &nbsp;
                            <Typography {...styles.discontinuedMsg}>
                                {translate("for more information about replacement items.")}
                            </Typography>
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default withProduct(ProductListProductCard);
